package com.fantasytenis.web.components

import androidx.compose.runtime.Composable
import com.fantasytenis.web.theme.Theme
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * A sticky CTA button that stays fixed at the bottom of the screen on mobile.
 * Used for primary actions that should always be visible.
 *
 * Includes iOS safe area support for devices with notch/home indicator.
 */
@Composable
fun StickyBottomCTA(
    text: String,
    onClick: () -> Unit,
    enabled: Boolean = true,
    subtitle: String? = null
) {
    val colors = Theme.colors

    Div({
        style {
            position(Position.Fixed)
            bottom(0.px)
            left(0.px)
            right(0.px)
            backgroundColor(colors.surface)
            property("box-shadow", "0 -2px 10px rgba(0, 0, 0, 0.1)")
            property("z-index", "100")
            // Padding with iOS safe area support
            padding(16.px)
            property("padding-bottom", "calc(16px + env(safe-area-inset-bottom, 0px))")
        }
    }) {
        // Main CTA button
        Div({
            style {
                backgroundColor(if (enabled) colors.primary else colors.surfaceVariant)
                color(if (enabled) colors.textOnPrimary else colors.textSecondary)
                padding(16.px, 24.px)
                borderRadius(12.px)
                fontSize(18.px)
                fontWeight(700)
                textAlign("center")
                cursor(if (enabled) "pointer" else "default")
                if (enabled) {
                    property("box-shadow", "0 4px 12px rgba(0, 166, 81, 0.3)")
                }
                property("transition", "transform 0.2s, box-shadow 0.2s, background-color 0.2s")
                // Minimum touch target
                minHeight(48.px)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
            }
            if (enabled) {
                onClick { onClick() }
            }
        }) {
            Text(text)
        }

        // Optional subtitle
        subtitle?.let {
            Div({
                style {
                    textAlign("center")
                    marginTop(8.px)
                    fontSize(13.px)
                    color(colors.textSecondary)
                }
            }) {
                Text(it)
            }
        }
    }
}

/**
 * A spacer to add at the bottom of scrollable content when using StickyBottomCTA.
 * This prevents the CTA from covering the last content item.
 */
@Composable
fun StickyBottomCTASpacer() {
    Div({
        style {
            // Height to account for CTA + safe area
            height(100.px)
            property("height", "calc(100px + env(safe-area-inset-bottom, 0px))")
        }
    })
}
